import { Fnv1a64 } from "../maths/fnv1a64";
import { Fix64 } from "../maths/fix64";
import type { LevelSim } from "./levelSim";
import type { PetRule } from "./petRule";
import { TroopBark } from "./troopRules";
import { tickBite } from "./petFighter";
import { tickBark } from "./petBark";
import { tickHowl } from "./petHowl";
import { tickStrike } from "./petStrike";
import { tickAura } from "./petAura";
import { tickPulse } from "./petPulse";
import { tickSignature } from "./petSignature";
import { tickPetStatuses } from "./petStatus";
import { Enemy } from "./entities";

/**
 * The pet on the rally flag in battle (Pets v2, docs/PETS.md): the run's PetRule (LevelModifiers.pet, its abilities'
 * numbers at its level) and the clocks of its abilities. Muster works through the troops' spawn interval (sim/pet.ts,
 * set when the run is built); Bite (sim/petFighter.ts), Rally Bark (sim/petBark.ts) and Howl (sim/petHowl.ts) run here
 * once per wave tick, in that order, after the raiders have moved and before the troops strike, all centred on the
 * flag's cell (TroopSystem.rally: the planted flag or the default rally). The sim needs no pet position: the view walks
 * the pet there. A run without a pet has no PetSystem, so it plays and hashes as it always did. Pets v3 (the five
 * newer pets): their passive (sim/petAura.ts), pulse (petPulse.ts), signature (petSignature.ts) and attack
 * (petStrike.ts) run after the pup's three, then the pets' statuses run down (petStatus.ts); `kit` says the run's pet
 * has any of them, and only then do combat's hooks and the hash's kit block run.
 */
export class PetSystem {
  /** Bite: ticks until it may bite again (0: ready). */
  biteCooldown = 0;
  /** Rally Bark: wave ticks until the next bark is due, and the ticks the current one has left (0: none on). */
  barkTimer: number;
  barkLeft = 0;
  /** Howl: the last stage it howled on (0: none yet); it is ready again once a later stage has started. */
  howlStage = 0;
  /** The bark as the troops read it (built once: no allocation per tick). */
  readonly barkBuff: TroopBark | null;
  /** Pets v3: whether the pet has any v3 ability (combat's hooks run only then); their clocks. */
  readonly kit: boolean;
  strikeCooldown = 0;
  pulseTimer: number;
  healTimer = 0;
  signatureStage = 0;
  /** Shell Wall as the troops read it, and the reach of the passive and of the pulse round the flag (built once). */
  readonly guardBuff: TroopBark | null;
  readonly reachBuff: TroopBark | null;
  readonly pulseBuff: TroopBark | null;
  /** @internal a list reused by the pulse and the signature (no allocation per tick). */
  readonly scratch: Enemy[] = [];

  constructor(readonly rule: PetRule) {
    this.kit = rule.strike !== undefined || rule.aura !== undefined || rule.pulse !== undefined || rule.signature !== undefined;
    this.pulseTimer = rule.pulse?.everyTicks ?? 0;
    const a = rule.aura, reach = (r: Fix64, taken = Fix64.One): TroopBark => ({ damageMult: Fix64.One, takenMult: taken, reach2: r.mul(r) });
    this.guardBuff = a && a.troopGuardPercent > 0 ? reach(a.reach, Fix64.fromInt(100 - a.troopGuardPercent).divInt(100)) : null;
    this.reachBuff = a ? reach(a.reach) : null;
    this.pulseBuff = rule.pulse ? reach(rule.pulse.reach) : null;
    const b = rule.bark;
    this.barkTimer = b?.everyTicks ?? 0;
    this.barkBuff = b ? {
      damageMult: Fix64.fromInt(100 + b.damagePercent).divInt(100),
      takenMult: Fix64.fromInt(100 - b.guardPercent).divInt(100),
      reach2: b.reach.mul(b.reach),
    } : null;
  }

  /** Whether Rally Bark is on now. */
  get barking(): boolean { return this.barkLeft > 0; }

  /** One wave tick: Rally Bark first (the troops strike under it this tick), then Howl, then Bite. */
  tick(sim: LevelSim): void {
    tickBark(sim, this);
    tickHowl(sim, this);
    tickBite(sim, this);
    if (!this.kit) return;
    tickAura(sim, this);
    tickPulse(sim, this);
    tickSignature(sim, this);
    tickStrike(sim, this);
    tickPetStatuses(sim);
  }

  writeHash(sim: LevelSim, h: Fnv1a64): void {
    h.writeInt(PET_SYSTEM_HASH_TAG); h.writeInt(this.biteCooldown); h.writeInt(this.barkTimer); h.writeInt(this.barkLeft); h.writeInt(this.howlStage);
    if (this.rule.howl) for (const e of sim.enemySlots) if (e.alive && e.stunTicksLeft > 0) { h.writeInt(e.id); h.writeInt(e.stunTicksLeft); }
    if (!this.kit) return;
    h.writeInt(PET_KIT_HASH_TAG); h.writeInt(this.strikeCooldown); h.writeInt(this.pulseTimer); h.writeInt(this.healTimer); h.writeInt(this.signatureStage);
    for (const e of sim.enemySlots) {
      if (!e.alive || (e.stunTicksLeft | e.petBurnTicks | e.markTicks | e.dazzleTicks) === 0) continue;
      h.writeInt(e.id); h.writeInt(e.stunTicksLeft); h.writeInt(e.petBurnTicks); h.writeRaw(e.petBurnPerTick.raw); h.writeInt(e.markTicks); h.writeRaw(e.markTaken.raw); h.writeInt(e.markPointsPercent); h.writeInt(e.dazzleTicks);
    }
    for (const t of sim.troops.slots) if (t.alive && t.shieldTicks > 0) { h.writeInt(t.id); h.writeRaw(t.shield.raw); h.writeInt(t.shieldTicks); }
  }
}

const PET_SYSTEM_HASH_TAG = 0x9e76, PET_KIT_HASH_TAG = 0x9e77;

/** The run's pet system, or null on a run without a pet. */
export function petSystemFor(rule: PetRule | null): PetSystem | null { return rule === null ? null : new PetSystem(rule); }
